/**
 * StaffAssignments Component
 * Staff assignment at planning level (Physio, Arzt, Athletik)
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stethoscope, HeartPulse, Dumbbell, Pencil, Trash2, Check, X } from 'lucide-react';
import {
  StaffAssignment,
  StaffRole,
  TravelerInfo
} from '../../types/matchdayPlanning';

interface StaffAssignmentsProps {
  assignments: StaffAssignment[];
  travelers: TravelerInfo[];
  onAdd: (assignment: Partial<StaffAssignment>) => void;
  onUpdate: (id: number, assignment: Partial<StaffAssignment>) => void;
  onDelete: (id: number) => void;
  isReadOnly?: boolean;
}

const STAFF_ROLE_ICON_COMPONENTS: Record<StaffRole, React.ReactNode> = {
  [StaffRole.Physio]: <HeartPulse size={14} />,
  [StaffRole.Arzt]: <Stethoscope size={14} />,
  [StaffRole.Athletik]: <Dumbbell size={14} />
};

const STAFF_ROLE_LABELS: Record<StaffRole, string> = {
  [StaffRole.Physio]: 'Physio',
  [StaffRole.Arzt]: 'Arzt',
  [StaffRole.Athletik]: 'Athletik'
};

const StaffAssignments: React.FC<StaffAssignmentsProps> = ({
  assignments,
  travelers,
  onAdd,
  onUpdate,
  onDelete,
  isReadOnly = false
}) => {
  const { t } = useTranslation(['common']);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<StaffAssignment>>({
    role: StaffRole.Physio,
    displayOrder: (assignments.length || 0) + 1
  });

  const handleAddClick = () => {
    setFormData({
      role: StaffRole.Physio,
      displayOrder: (assignments.length || 0) + 1
    });
    setIsAdding(true);
    setEditingId(null);
  };

  const handleEditClick = (assignment: StaffAssignment) => {
    setFormData({
      travelerId: assignment.travelerId,
      role: assignment.role,
      notes: assignment.notes,
      displayOrder: assignment.displayOrder
    });
    setEditingId(assignment.id);
    setIsAdding(false);
  };

  const handleSave = () => {
    if (isAdding) {
      onAdd(formData);
    } else if (editingId !== null) {
      onUpdate(editingId, formData);
    }
    setIsAdding(false);
    setEditingId(null);
    setFormData({ role: StaffRole.Physio });
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({ role: StaffRole.Physio });
  };

  const getTravelerName = (assignment: StaffAssignment): string => {
    if (assignment.traveler) {
      return assignment.traveler.fullName;
    }
    if (assignment.travelerId) {
      const traveler = travelers.find(t => t.id === assignment.travelerId);
      if (traveler) return traveler.fullName;
    }
    return '-';
  };

  // Inline styles for compact single-line display
  const sectionStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '8px',
    padding: '12px 16px',
    marginTop: '16px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap'
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexShrink: 0
  };

  const titleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#424242',
    whiteSpace: 'nowrap'
  };

  const assignmentsRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    flex: 1
  };

  const assignmentChipStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: '20px',
    padding: '6px 12px',
    fontSize: '0.85rem'
  };

  const chipIconStyle: React.CSSProperties = {
    fontSize: '1rem'
  };

  const chipTextStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const emptyTextStyle: React.CSSProperties = {
    color: '#9e9e9e',
    fontStyle: 'italic',
    fontSize: '0.85rem'
  };

  const chipActionsStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginLeft: '4px'
  };

  const smallBtnStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '2px',
    fontSize: '0.75rem',
    lineHeight: 1,
    opacity: 0.7
  };

  return (
    <div style={sectionStyle}>
      <div style={headerStyle}>
        <h4 style={titleStyle}><Stethoscope size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> {t('common:planning.staffAssignments', 'Staff-Zuordnungen')}</h4>
        {!isReadOnly && (
          <button type="button" className="btn btn-sm btn-primary" onClick={handleAddClick} style={{ padding: '4px 10px', fontSize: '0.8rem' }}>
            + {t('common:planning.addStaff', 'Staff hinzufügen')}
          </button>
        )}
      </div>

      <div style={assignmentsRowStyle}>
        {assignments.length === 0 && !isAdding && (
          <span style={emptyTextStyle}>{t('common:planning.noStaffAssigned', 'Noch kein Staff zugeordnet')}</span>
        )}

        {/* Existing assignments as chips */}
        {assignments.map((assignment) => (
          <div key={assignment.id}>
            {editingId === assignment.id ? (
              <div className="assignment-card editing" style={{ background: '#fff', border: '1px solid #1976d2', borderRadius: '8px', padding: '12px' }}>
                <StaffAssignmentForm
                  formData={formData}
                  setFormData={setFormData}
                  travelers={travelers}
                  onSave={handleSave}
                  onCancel={handleCancel}
                />
              </div>
            ) : (
              <div style={assignmentChipStyle}>
                <span style={chipIconStyle}>{STAFF_ROLE_ICON_COMPONENTS[assignment.role]}</span>
                <span style={chipTextStyle}>
                  <strong>{STAFF_ROLE_LABELS[assignment.role]}</strong>
                  <span style={{ color: '#666' }}>{getTravelerName(assignment)}</span>
                </span>
                {!isReadOnly && (
                  <span style={chipActionsStyle}>
                    <button type="button" style={smallBtnStyle} onClick={() => handleEditClick(assignment)} title="Bearbeiten"><Pencil size={12} /></button>
                    <button type="button" style={smallBtnStyle} onClick={() => onDelete(assignment.id)} title="Löschen"><Trash2 size={12} /></button>
                  </span>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Add new assignment form */}
        {isAdding && (
          <div className="assignment-card adding" style={{ background: '#fff', border: '1px solid #1976d2', borderRadius: '8px', padding: '12px' }}>
            <StaffAssignmentForm
              formData={formData}
              setFormData={setFormData}
              travelers={travelers}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Form component for staff assignment
interface StaffAssignmentFormProps {
  formData: Partial<StaffAssignment>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<StaffAssignment>>>;
  travelers: TravelerInfo[];
  onSave: () => void;
  onCancel: () => void;
}

const StaffAssignmentForm: React.FC<StaffAssignmentFormProps> = ({
  formData,
  setFormData,
  travelers,
  onSave,
  onCancel
}) => {
  const { t } = useTranslation(['common']);

  return (
    <div className="assignment-form">
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="staffRole">{t('common:planning.staffRole', 'Rolle')}</label>
          <select
            id="staffRole"
            value={formData.role ?? StaffRole.Physio}
            onChange={(e) => setFormData({ ...formData, role: parseInt(e.target.value) as StaffRole })}
          >
            {Object.entries(STAFF_ROLE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="staffTraveler">{t('common:planning.staffMember', 'Person')}</label>
          <select
            id="staffTraveler"
            value={formData.travelerId || ''}
            onChange={(e) => setFormData({ ...formData, travelerId: e.target.value ? parseInt(e.target.value) : undefined })}
          >
            <option value="">-- Person auswählen --</option>
            {travelers.map(t => (
              <option key={t.id} value={t.id}>{t.fullName}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group full-width">
          <label htmlFor="staffNotes">{t('common:planning.notes', 'Notizen')}</label>
          <input
            id="staffNotes"
            type="text"
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Optionale Notizen..."
          />
        </div>
      </div>
      <div className="form-actions">
        <button type="button" className="btn btn-primary btn-sm" onClick={onSave}>
          <Check size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> {t('common:save', 'Speichern')}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>
          <X size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> {t('common:cancel', 'Abbrechen')}
        </button>
      </div>
    </div>
  );
};

export default StaffAssignments;
